"use client";

import type {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  RefObject,
} from "react";
import { BuilderDeviceFrame } from "./BuilderDeviceFrame";
import type { BuilderStudioDevice } from "./types";
import "./builderInspector.css";

type BuilderCanvasStyle = CSSProperties & {
  "--builder-inspector-width"?: string;
};

export type BuilderCanvasShellProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children" | "title"
> & {
  canvasRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
  device: BuilderStudioDevice;
  frameClassName?: string;
  frameRef?: RefObject<HTMLIFrameElement | null>;
  frameTitle: string;
  inspectorOpen?: boolean;
  inspectorSpaceReserved?: boolean;
  inspectorWidth?: number;
  onFrameDocumentReady?: (frameDocument: Document) => void;
  overlay?: ReactNode;
};

export function BuilderCanvasShell({
  canvasRef,
  children,
  className = "",
  device,
  frameClassName,
  frameRef,
  frameTitle,
  inspectorOpen = false,
  inspectorSpaceReserved = inspectorOpen,
  inspectorWidth = 340,
  onFrameDocumentReady,
  overlay,
  style,
  ...props
}: BuilderCanvasShellProps) {
  const canvasStyle: BuilderCanvasStyle = {
    ...style,
    "--builder-inspector-width": `${Math.max(0, inspectorWidth)}px`,
  };

  return (
    <div
      {...props}
      ref={canvasRef}
      className={[
        "builder-studio-canvas relative h-full min-h-0 overflow-auto bg-slate-100",
        className,
      ].filter(Boolean).join(" ")}
      data-builder-canvas="true"
      data-builder-inspector-open={inspectorOpen ? "true" : "false"}
      data-builder-inspector-space-reserved={
        inspectorSpaceReserved ? "true" : "false"
      }
      style={canvasStyle}
    >
      <div
        className="h-full min-h-full"
        data-builder-device-frame-host="true"
      >
        <BuilderDeviceFrame
          className={frameClassName}
          device={device}
          frameRef={frameRef}
          onFrameDocumentReady={onFrameDocumentReady}
          title={frameTitle}
        >
          {children}
        </BuilderDeviceFrame>
      </div>
      {overlay ? (
        <div
          className="pointer-events-none absolute inset-0"
          data-builder-canvas-overlay="true"
        >
          {overlay}
        </div>
      ) : null}
    </div>
  );
}
